/**
 * Norel product gallery autoplay.
 *
 * - Photos stay 5 s, then the gallery moves to the next photo or video.
 * - Videos play (muted) and the gallery moves on when the video ends.
 * - When the customer touches, clicks, swipes or scrolls the gallery, autoplay stops.
 *   It starts again 30 s after the last touch, or 30 s after the current video ends
 *   if a video is playing.
 * - Nothing moves while the gallery is off screen. (Runs even with "Reduce Motion" on, by choice.)
 * - Where the browser blocks autoplay (iPhone Low Power Mode), the video starts on the first tap.
 */

const IMAGE_DELAY = 5000;
const RESUME_DELAY = 30000;

class NorelGalleryAutoplay {
  /** @param {HTMLElement} slideshow - the <slideshow-component> of the product gallery */
  constructor(slideshow) {
    this.slideshow = slideshow;
    this.timer = 0;
    this.resumeTimer = 0;
    this.userPaused = false;
    this.visible = true;
    this.video = null;

    const interact = () => this.onInteraction();
    for (const type of ['pointerdown', 'touchstart', 'wheel', 'keydown']) {
      slideshow.addEventListener(type, interact, { passive: true });
    }
    slideshow.addEventListener('slideshow:select', (event) => {
      if (event.detail?.userInitiated) this.onInteraction();
      else this.schedule();
    });

    new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.visible ? this.schedule() : this.stop();
    }).observe(slideshow);
    document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.schedule()));

    this.schedule();
  }

  get slides() {
    return this.slideshow.slides || [];
  }

  get currentSlide() {
    return this.slides[this.slideshow.current] || null;
  }

  stop() {
    clearTimeout(this.timer);
    this.timer = 0;
  }

  /** Plans the next move for the slide on screen. */
  schedule() {
    this.stop();
    this.stopOtherVideos();
    if (this.userPaused || !this.visible || document.hidden || !this.slides.length) return;

    const deferred = this.currentSlide?.querySelector('deferred-media');
    if (deferred) {
      this.playVideo(deferred);
    } else if (this.slides.length > 1) {
      this.timer = window.setTimeout(() => this.next(), IMAGE_DELAY);
    }
  }

  next() {
    const count = this.slides.length;
    if (count < 2) {
      // A product with a single video: play it again.
      const deferred = this.currentSlide?.querySelector('deferred-media');
      if (deferred) this.playVideo(deferred);
      return;
    }
    this.slideshow.select((this.slideshow.current + 1) % count);
  }

  /** @param {HTMLElement & { loadContent?: Function, updatePlayPauseHint?: Function }} deferred */
  playVideo(deferred) {
    // iOS Safari only autoplays a video that is muted *and* inline. It ignores the `muted`
    // attribute on videos cloned from a <template>, so set it on the template copy first.
    const templateVideo = deferred.querySelector('template')?.content.querySelector('video');
    if (templateVideo) prepareInline(templateVideo);
    if (!deferred.getAttribute('data-media-loaded')) deferred.loadContent?.(false);

    const videos = deferred.querySelectorAll('video');
    const video = videos[videos.length - 1];
    if (!video) {
      // External videos (YouTube/Vimeo) don't report their end: treat them like a photo.
      this.timer = window.setTimeout(() => this.next(), IMAGE_DELAY);
      return;
    }
    this.video = video;
    prepareInline(video);
    video.loop = false;
    if (video.readyState > 0) video.currentTime = 0;
    video.onended = () => {
      if (this.video === video && !this.userPaused) this.next();
    };
    video
      .play()
      .then(() => deferred.updatePlayPauseHint?.(true))
      .catch(() => {
        // Autoplay refused (e.g. iPhone in Low Power Mode): browsers still allow playback started
        // by a tap, so start the video on the visitor's first tap or click anywhere on the page.
        deferred.updatePlayPauseHint?.(false);
        const retry = () => {
          document.removeEventListener('touchend', retry, true);
          document.removeEventListener('click', retry, true);
          if (this.video !== video || !this.currentSlide?.contains(video)) return;
          video.play().then(() => deferred.updatePlayPauseHint?.(true)).catch(() => {});
        };
        document.addEventListener('touchend', retry, { capture: true, passive: true });
        document.addEventListener('click', retry, { capture: true });
        if (this.slides.length > 1) this.timer = window.setTimeout(() => this.next(), IMAGE_DELAY);
      });
  }

  stopOtherVideos() {
    const current = this.currentSlide;
    for (const slide of this.slides) {
      if (slide === current) continue;
      slide.querySelectorAll('video').forEach((video) => video.pause());
    }
    if (this.video && !current?.contains(this.video)) this.video = null;
  }

  /** The customer touched the gallery: stop, then restart 30 s later (after the video, if one plays). */
  onInteraction() {
    this.userPaused = true;
    this.stop();
    clearTimeout(this.resumeTimer);
    const video = this.video;
    const resume = () => {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => {
        this.userPaused = false;
        this.schedule();
      }, RESUME_DELAY);
    };
    if (video && !video.paused && !video.ended && this.currentSlide?.contains(video)) {
      video.onended = () => {
        if (this.video === video) resume();
      };
    } else {
      resume();
    }
  }
}

/** @param {HTMLVideoElement} video */
function prepareInline(video) {
  video.muted = true;
  video.defaultMuted = true;
  video.playsInline = true;
  video.setAttribute('muted', '');
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');
  video.setAttribute('autoplay', '');
}

function init() {
  document.querySelectorAll('media-gallery slideshow-component').forEach((slideshow) => {
    if (slideshow.dataset.norelAutoplay) return;
    slideshow.dataset.norelAutoplay = 'true';
    customElements.whenDefined('slideshow-component').then(() => new NorelGalleryAutoplay(slideshow));
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
